/** @format */

import { memo } from 'react'
import { CopyBlock, ocean } from 'react-code-blocks'

import styles from 'dir/styles/courses.module.css'

export const _8 = memo(() => (
  <section className={styles.section}>
    <h2 className={styles.h2}>1 - Введение</h2>
    <p className={styles.description}>
      Все числа в JavaScript, как целые так и дробные, имеют тип <code className={styles.code}>Number</code> и
      записывать их можно не только в десятичной системе счисления.
    </p>

    <section className={styles.subsection}>
      <h2 className={styles.h2}>2 - Приведение к числу</h2>
      <p className={styles.description}>
        Большинство арифметических операций и математических функций преобразуют значение в число автоматически. Для
        того чтобы сделать это явно, используйте функцию <code className={styles.code}>Number(val)</code>, передавая ей
        в <code className={styles.code}>val</code> то, что надо привести к числу.
      </p>
      <p className={styles.description}>
        Если значние не возможно привести к числу, результатом будет специальное значение{' '}
        <code className={styles.code}>NaN (Not a Number)</code>. Аналогичным образом происходит преобразование и в
        других математических операторах и функциях.
      </p>
      <CopyBlock
        text={`const valueA = '5';
console.log(Number(valueA)); // 5
console.log(typeof Number(valueA)); // 'number'

const valueB = 'random string';
console.log(Number(valueB)); // NaN
console.log(typeof Number(valueB)); // 'number'`}
        language="js"
        showLineNumbers={false}
        theme={ocean}
      />
    </section>

    <section className={styles.subsection}>
      <h2 className={styles.h2}>3 - Number.parseInt() и Number.parseFloat()</h2>
      <p className={styles.description}>
        Методы преобразуют строку символ за символом, пока это возможно. При возникновении ошибки возвращается число,
        которое получилось.
      </p>
      <CopyBlock
        text={`// Парсит из строки целое число
console.log(Number.parseInt('5px')); // 5
console.log(Number.parseInt('12qwe74')); // 12
console.log(Number.parseInt('12.46qwe79')); // 12
console.log(Number.parseInt('qweqwe')); // NaN

// Парсит из строки дробное число
console.log(Number.parseFloat('5px')); // 5
console.log(Number.parseFloat('12qwe74')); // 12
console.log(Number.parseFloat('12.46qwe79')); // 12.46
console.log(Number.parseFloat('qweqwe')); // NaN
`}
        language="js"
        showLineNumbers={false}
        theme={ocean}
      />
    </section>

    <section className={styles.subsection}>
      <h2 className={styles.h2}>4 - Проверка на число</h2>
      <p className={styles.description}>
        Для проверки на число можно использовать метод <code className={styles.code}>Number.isNaN(val)</code>. Он
        проверяет, является ли указанное значение <code className={styles.code}>NaN</code> или нет. Этот метод отвечает
        на вопрос <i>"Это Not A Number?"</i> и возвращает:
      </p>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          <code className={styles.code}>true</code> — если значение <code className={styles.code}>val</code> это{' '}
          <code className={styles.code}>NaN</code>
        </li>
        <li className={styles.list__item}>
          <code className={styles.code}>false</code> — если значение <code className={styles.code}>val</code> это не{' '}
          <code className={styles.code}>NaN</code>
        </li>
      </ul>
      <p className={styles.description}>
        Для всех значений <code className={styles.code}>val</code> кроме <code className={styles.code}>NaN</code>, при
        передаче в <code className={styles.code}>Number.isNaN(val)</code> вернёт
        <code className={styles.code}>false</code>. Этот метод не производит попытку преобразовать{' '}
        <code className={styles.code}>val</code> к числу, а просто выполняет проверку на{' '}
        <code className={styles.code}>NaN</code>.
      </p>
      <CopyBlock
        text={`const validNumber = Number('51'); // 51
console.log(Number.isNaN(validNumber)); // false

const invalidNumber = Number('qweqwe'); // NaN
console.log(Number.isNaN(invalidNumber)); // true
`}
        language="js"
        showLineNumbers={false}
        theme={ocean}
      />
    </section>

    <section className={styles.subsection}>
      <h2 className={styles.h2}>5 - Сложение чисел с плавающей точкой</h2>
      <p className={styles.description}>
        При сложении не целых чисел в JavaScript, и других языках программирования, есть особенность. Если кратко, то{' '}
        <code className={styles.code}>0.1 + 0.2</code> не равно <code className={styles.code}>0.3</code>, результат
        сложения больше чем <code className={styles.code}>0.3</code>. Все от того что машина считает в двоичной системе.
      </p>
      <p className={styles.description}>
        Число <code className={styles.code}>0.1</code> в двоичной системе счисления - это бесконечная дробь, так как
        единица на десять в двоичной системе не делится. Также бесконечной дробью является{' '}
        <code className={styles.code}>0.2</code>. Двоичное значение бесконечных дробей хранится только до определенного
        знака, поэтому возникает неточность. При сложении <code className={styles.code}>0.1</code> и{' '}
        <code className={styles.code}>0.2</code>, две неточности складываются, получается незначительная, но всё же
        ошибка в вычислениях.
      </p>
      <CopyBlock
        text={`console.log(0.1 + 0.2 === 0.3); // false

console.log(0.1 + 0.2); // 0.30000000000000004
`}
        language="js"
        showLineNumbers={false}
        theme={ocean}
      />
      <p className={styles.description}>
        Конечно, это не означает, что точные вычисления для таких чисел невозможны. Есть несколько методов решения этой
        проблемы:
      </p>
      <ul className={styles.list}>
        <li className={styles.list__item}>
          Сделать их целыми, умножив на 10, сложить, а потом результат разделить на 10
        </li>
        <li className={styles.list__item}>
          Сложить, а затем округлить до разумного знака после запятой. Округления до 10-го знака обычно бывает
          достаточно, чтобы отсечь ошибку вычислений
        </li>
      </ul>
      <CopyBlock
        text={`// Сделать их целыми, умножив на 10, сложить, а результат разделить на 10
console.log((0.1 * 10 + 0.2 * 10) / 10);
`}
        language="js"
        showLineNumbers={false}
        theme={ocean}
      />
    </section>

    <section className={styles.subsection}>
      <h2 className={styles.h2}>6 - Объект Math</h2>
      <p className={styles.description}>
        Один из встроенных объектов в язык. Предоставляет набор методов для работы с числами. Знание всех методов
        наизусть не требуется, только некоторых, наиболее полезных.
      </p>
      <CopyBlock
        text={`//  Math.floor(num) - возвращает наибольшее целое число, меньшее, либо равное указанному
console.log(Math.floor(1.7)); // 1

// Math.ceil(num) - возвращает наименьшее целое число,
// большее, либо равное указанному числу.
console.log(Math.ceil(1.2)); // 2

// Math.round(num) - возвращает значение числа, округлённое до ближайшего целого
console.log(Math.round(1.2)); // 1
console.log(Math.round(1.5)); // 2

// Math.max(num1, num2, ...) - возвращает наибольшее число из набора
console.log(Math.max(20, 10, 50, 40)); // 50

// Math.min(num1, num2, ...) - возвращает наименьшее число из набора
console.log(Math.min(20, 10, 50, 40)); // 10

// Math.pow(base, exponent) - возведение в степень
console.log(Math.pow(2, 4)); // 16

// Math.random() - возвращает псевдослучайное число в диапазоне [0, 1)
console.log(Math.random()); // случайное число между 0 и 1
console.log(Math.random() * (10 - 1) + 1); // случайное число от 1 до 10
`}
        language="js"
        showLineNumbers={false}
        theme={ocean}
      />
      <a
        className={styles.link}
        rel="noopener noreferrer"
        href="https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Math"
        target="_blank"
      >
        Документация Math на MDN
      </a>
    </section>
  </section>
))
